import React from 'react';
import { Badge } from '../../../../../components/badge';
import type { SurveySummaryQuestion } from '../../types/survey.types';

interface YesNoResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const YesNoResponse: React.FC<YesNoResponseProps> = ({
    question,
    totalResponses
}) => {
    const { position, title, type, answered_count, options } = question;

    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';

    // Find Yes and No options
    const yesOption = options.find(option => option.label.toLowerCase() === 'yes');
    const noOption = options.find(option => option.label.toLowerCase() === 'no');

    // Calculate percentages
    const yesCount = yesOption?.count || 0;
    const noCount = noOption?.count || 0;
    const yesPercentage = answered_count > 0 ? ((yesCount / answered_count) * 100).toFixed(0) : '0';
    const noPercentage = answered_count > 0 ? ((noCount / answered_count) * 100).toFixed(0) : '0';

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 p-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start gap-3 mb-6">
                {/* Question Icon */}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.7674 8.75349C19.148 10.6212 18.8768 12.563 17.999 14.255C17.1212 15.947 15.6898 17.2869 13.9437 18.0513C12.1975 18.8156 10.2421 18.9583 8.40352 18.4555C6.56491 17.9526 4.95426 16.8347 3.84017 15.288C2.72607 13.7414 2.17587 11.8595 2.28132 9.95631C2.38677 8.0531 3.1415 6.24357 4.41965 4.82947C5.69779 3.41537 7.42209 2.48218 9.30499 2.18554C11.1879 1.88889 13.1156 2.24671 14.7666 3.19932" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8.09961 9.58675L10.5996 12.0868L18.9329 3.75342" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>

                <div className="flex-1">
                    {/* Question Title */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {position}. {title}
                    </h3>

                    {/* Question Type Badge and Response Info */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <Badge
                            variant="outline"
                            className="text-[10px] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800"
                        >
                            {type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </Badge>

                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{answered_count}</span> of <span className="font-medium">{totalResponses}</span> responses
                        </div>

                        <div className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                            {responseRate}% response rate
                        </div>
                    </div>
                </div>
            </div>

            {/* Yes/No Response Display */}
            <div className="grid grid-cols-2 gap-6">
                {/* Yes Section */}
                <div className="flex flex-col items-center justify-center p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                        {yesCount}
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        Yes
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                        {yesPercentage}%
                    </div>
                </div>

                {/* No Section */}
                <div className="flex flex-col items-center justify-center p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                        {noCount}
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        No
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                        {noPercentage}%
                    </div>
                </div>
            </div>

            {/* No responses message */}
            {options.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No responses available for this question
                </div>
            )}
        </div>
    );
};

export default YesNoResponse;
